import type { Ring, Vec2 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { coneSolid, prismSolid, pyramidOverRing } from '../../solids';
import { roofTrim } from './fortressParts';
import { panel } from './fortressBlocks';

/** Parts that dress a round tower: dormers on its roof, corbels under a gallery, pinnacles on a parapet. */

function along(centre: Vec2, azimuth: number, radial: number, lateral: number): Vec2 {
  const [c, s] = [Math.cos(azimuth), Math.sin(azimuth)];
  return [centre[0] + c * radial - s * lateral, centre[1] + s * radial + c * lateral];
}

/** The plan ring of a block standing on a ray from `centre`, from radius r0 out to r1, `width` wide. */
export function radialBlock(
  centre: Vec2,
  azimuth: number,
  r: readonly [number, number],
  width: number
): Ring {
  const w = width / 2;
  return [
    along(centre, azimuth, r[0], -w),
    along(centre, azimuth, r[1], -w),
    along(centre, azimuth, r[1], w),
    along(centre, azimuth, r[0], w),
  ];
}

export interface DormerSpec {
  readonly azimuth: number;
  /** Radii of its back, buried in the roof, and of its front. */
  readonly r: readonly [number, number];
  readonly width: number;
  readonly sill: number;
  readonly eave: number;
  readonly peak: number;
  readonly spikeTop: number;
  /** Width of its window, or none. */
  readonly window?: number;
}

/*
 * A dormer clad as its roof (shaded as stone, since its cheeks shed no rain), under a pyramid cap that
 * closes it and a spike; the window a dark panel on its front.
 */
export function dormer(
  builder: MeshBuilder,
  styles: { readonly roof: VertexStyle; readonly dark: VertexStyle },
  frame: Frame,
  centre: Vec2,
  spec: DormerSpec
): void {
  const ring = radialBlock(centre, spec.azimuth, spec.r, spec.width);
  const cheeks = roofTrim(styles.roof);
  prismSolid(builder, cheeks, frame, ring, spec.sill, spec.eave, { top: false, bottom: true });
  const tip = along(centre, spec.azimuth, (spec.r[0] + spec.r[1]) / 2, 0);
  pyramidOverRing(builder, { roof: styles.roof, wall: cheeks }, frame, ring, spec.eave, {
    plan: tip,
    y: spec.peak,
  });
  const spike = { center: tip, radius: 0.04, segments: 4, phase: 0 };
  coneSolid(builder, cheeks, frame, spike, spec.peak - 0.1, spec.spikeTop, true);
  if (spec.window === undefined) return;
  const rise = spec.eave - spec.sill;
  panel(builder, styles.dark, frame, {
    at: along(centre, spec.azimuth, spec.r[1], 0),
    facing: spec.azimuth,
    width: spec.window,
    sill: spec.sill + 0.2 * rise,
    head: spec.eave - 0.2 * rise,
    arch: false,
    proud: 0.04,
    depth: 0.2,
  });
}

/** Corbels on rays round a tower, a quarter of the way into its wall and out to `reach` beyond it. */
export function radialCorbels(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  tower: { readonly centre: Vec2; readonly radius: number },
  spec: { readonly count: number; readonly width: number; readonly reach: number },
  y: readonly [number, number]
): void {
  for (let k = 0; k < spec.count; k++) {
    const azimuth = ((k + 0.5) / spec.count) * 2 * Math.PI;
    const r: [number, number] = [tower.radius - 0.1, tower.radius + spec.reach];
    const ring = radialBlock(tower.centre, azimuth, r, spec.width);
    prismSolid(builder, style, frame, ring, y[0], y[1], { top: true, bottom: true });
  }
}

/** Four-sided pinnacles standing on a ring at the given azimuths. */
export function pinnacles(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  on: { readonly centre: Vec2; readonly radius: number; readonly azimuths: readonly number[] },
  spec: { readonly base: number; readonly top: number; readonly radius: number }
): void {
  for (const azimuth of on.azimuths) {
    const center = along(on.centre, azimuth, on.radius, 0);
    const cone = { center, radius: spec.radius, segments: 4, phase: azimuth + Math.PI / 4 };
    coneSolid(builder, style, frame, cone, spec.base, spec.top, true);
  }
}

/** A flag on a spire's rod, standing out along `azimuth`, its top `below` under the rod's top. */
export function vane(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  at: { readonly centre: Vec2; readonly top: number; readonly azimuth: number },
  spec: {
    readonly below: number;
    readonly height: number;
    readonly length: number;
    readonly thickness: number;
  }
): void {
  const ring = radialBlock(at.centre, at.azimuth, [0, spec.length], spec.thickness);
  const top = at.top - spec.below;
  prismSolid(builder, style, frame, ring, top - spec.height, top, { top: true, bottom: true });
}
